import { theme } from 'antd';

const BRAND = {
  primary: '#D3001A',
  primaryHover: '#B80017',
  primaryActive: '#A00014',
  surface: '#FFFFFF',
  surfaceMuted: '#FAFAFA',
  border: '#E8E8E8',
  text: '#1F1F1F',
  textMuted: '#6B7280',
  success: '#16A34A',
  warning: '#F59E0B',
  danger: '#DC2626',
  info: '#2563EB',
};

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const antdTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: BRAND.primary,
    colorInfo: BRAND.info,
    colorSuccess: BRAND.success,
    colorWarning: BRAND.warning,
    colorError: BRAND.danger,

    colorText: BRAND.text,
    colorTextSecondary: BRAND.textMuted,
    colorBgLayout: BRAND.surfaceMuted,
    colorBorder: BRAND.border,
    colorBorderSecondary: '#F0F0F0',

    borderRadius: 10,
    borderRadiusLG: 14,
    borderRadiusSM: 6,

    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,

    fontFamily: FONT,
    fontSize: 14,
    lineHeight: 1.55,

    boxShadow:
      '0 1px 2px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)',
    boxShadowSecondary:
      '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',

    // antd's default popup base is 1000, but this app's legacy chrome sits
    // above it — the header is `position: fixed; z-index: 1044`
    // (assets/scss/layout/_header.scss:6) and the sidebar is 1040/1041
    // (layout/_sidebar.scss:282,14). At the default, every antd Modal, Drawer,
    // Select dropdown and DatePicker panel renders UNDER the header and
    // sidebar, which hid the title of any modal tall enough to reach the top
    // of the viewport. Raising the base lifts the whole antd popup family over
    // that chrome in one place. Kept below react-toastify (9999) so error
    // toasts still surface above an open modal.
    zIndexPopupBase: 1100,

    wireframe: false,
  },
  components: {
    Button: {
      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,
      fontWeight: 600,
      controlHeight: 40,
      paddingInline: 20,
      primaryShadow: '0 1px 2px rgba(211,0,26,0.16)',
      defaultShadow: 'none',
    },
    Input: {
      borderRadius: 10,
      controlHeight: 40,
      paddingInline: 12,
      activeShadow: '0 0 0 3px rgba(211,0,26,0.10)',
    },
    InputNumber: {
      borderRadius: 10,
      controlHeight: 40,
    },
    Select: {
      borderRadius: 10,
      controlHeight: 40,
      optionSelectedBg: 'rgba(211,0,26,0.08)',
      optionSelectedColor: BRAND.primary,
    },
    DatePicker: {
      borderRadius: 10,
      controlHeight: 40,
    },
    Card: {
      borderRadiusLG: 14,
      paddingLG: 20,
      headerFontSize: 16,
      headerFontSizeSM: 14,
    },
    Modal: {
      borderRadiusLG: 16,
      paddingContentHorizontalLG: 24,
      titleFontSize: 18,
    },
    // These tokens are only authoritative because the hand-written rules that
    // used to outrank them were retired alongside this block (see
    // `assets/scss/pages/react.scss` and the `!important`s dropped from
    // `theme/theme-overrides.css`).
    //
    // Do NOT assume antd's emitted CSS is weak. Verified against the installed
    // antd 5.29.3 / @ant-design/cssinjs 1.24.0: `injectSelectorHash`
    // (`cssinjs/lib/hooks/useStyleRegister.js:49-68`) wraps *only the hash
    // class* in `:where()`, and splices it into the first compound of the
    // selector. The rest keeps its natural weight, so a token rule ships as
    // e.g. `:where(.css-1a2b3c).ant-table-wrapper .ant-table-tbody > tr > td`
    // — specificity (0,2,2), not (0,1,0).
    //
    // App CSS wins on ORDER, not strength: cssinjs injects with
    // `prepend: 'queue'` (same file, ~L327/L416), so antd's <style> sits at the
    // top of <head> and every stylesheet `index.jsx` imports comes after it.
    // Ties therefore go to app CSS. That is the whole mechanism, and it is
    // fragile in one specific way: a rule that is *weaker* than the token
    // selector loses regardless of order. `.custom-table td` (0,1,1) already
    // loses to the (0,2,2) above — see `_tables.scss:144`. So if a value here
    // stops rendering, look for a stylesheet that came back; and if a new
    // hand-written rule fails to apply, count its specificity before assuming
    // order will save it.
    Table: {
      headerBg: '#F9FAFB',
      headerColor: '#6B7280',
      headerSplitColor: 'transparent',
      // 0, not 12: the surface that owns the radius is now `.list-shell`
      // (or, for a standalone table, `.ant-table` itself). A rounded header
      // inside a clipped, rounded card never rendered anyway.
      headerBorderRadius: 0,
      borderColor: '#F1F2F4',
      rowHoverBg: '#FBFBFC',
      // 14, not 16: 16 is airy for a dense operations grid and the rendered
      // value was 10 (react.scss forced it). 14 gives a ~44px row at
      // 14px/1.55, which clears the touch-target minimum.
      cellPaddingBlock: 14,
      cellPaddingInline: 20,
      // Compact scale for the line-item grids embedded in form slide-overs
      // and dialogs, reached with `size="small"`. Same skin, tighter rhythm —
      // only the padding moves, every colour and border is inherited.
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 12,
      cellFontSize: 14,
      headerSortHoverBg: '#F3F4F6',
      headerSortActiveBg: '#F3F4F6',
      // White, not #FAFAFA: the footer sits inside the card now, so a tinted
      // strip would read as a second surface.
      footerBg: '#FFFFFF',
      bodySortBg: 'transparent',
    },
    // SCOPE: these tokens no longer reach the Uniform Management list pages.
    // Their status strip is not a <Tabs> any more — the tab semantics were a
    // lie (the table was never in a tabpanel), so `components/ListShell.jsx`
    // now renders a labelled group of `aria-pressed` buttons styled by
    // `.list-shell-filter*` in theme-overrides.css. The values below were
    // tuned for that strip and are kept because the remaining <Tabs> in the
    // app — MastersDashboard and ReportsDashboard, both `type="card"` —
    // inherit them, and matching the two treatments is still what we want.
    // The `.list-shell-filter` CSS deliberately reproduces this geometry;
    // if you change one, change the other.
    Tabs: {
      itemColor: BRAND.textMuted,
      // Near-black, not brand red. On these list pages red already carries the
      // record links, the status badges and the primary button; a red selected
      // label would be the sixth red element and dilute red's meaning as
      // "primary action". The ink bar alone is the brand signal.
      itemSelectedColor: BRAND.text,
      itemHoverColor: BRAND.text,
      inkBarColor: BRAND.primary,
      titleFontSize: 14,
      // 14px block padding gives a 48px strip — the same as `controlHeightLG`,
      // so the strip reads as a control rather than a heading.
      horizontalItemPadding: '14px 0',
      // 24, not 28: six statuses (Issuance) have to fit at 1280px without
      // the strip scrolling.
      horizontalItemGutter: 24,
      // NOTE: antd's default `horizontalMargin` (`0 0 16px 0`) is deliberately
      // left alone. The design called for zeroing it so the strip's hairline
      // could double as the table header's top border — but the token lands on
      // every top/bottom Tabs, `type="card"` included
      // (antd/lib/tabs/style/index.js:210-213), and the two dashboards above
      // are card tabs whose panel content would then butt straight against the
      // strip. The list shell no longer needs the override at all: it owns its
      // own strip and sets no margin on it.
    },
    Tag: {
      borderRadiusSM: 999,
      defaultBg: '#F4F4F5',
      defaultColor: '#3F3F46',
    },
    Tooltip: {
      borderRadius: 8,
      colorBgSpotlight: '#1F1F1F',
    },
    Drawer: {
      paddingLG: 20,
    },
    Notification: {
      borderRadiusLG: 12,
    },
    Pagination: {
      itemActiveBg: BRAND.primary,
      itemActiveColorDisabled: '#fff',
    },
    Form: {
      labelFontSize: 13,
      labelColor: '#374151',
      itemMarginBottom: 18,
      verticalLabelPadding: '0 0 6px',
    },
    Breadcrumb: {
      lastItemColor: BRAND.text,
      itemColor: BRAND.textMuted,
    },
  },
};

export default antdTheme;
